import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { fiscalYearPeriodKey } from '../periods/keys';
import { lockPeriod, lockedThroughFor } from '../periods/service';
import type { FiscalYear, IsoDate, Uuid } from '../types';

export class FiscalYearError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FiscalYearError';
  }
}

export function fiscalYearLabel(startDate: IsoDate, endDate: IsoDate): string {
  const startYear = startDate.slice(0, 4);
  const endYear = endDate.slice(0, 4);
  return startYear === endYear ? startYear : `${startYear}/${endYear}`;
}

export function isWithin(date: IsoDate, fy: Pick<FiscalYear, 'startDate' | 'endDate'>): boolean {
  return date >= fy.startDate && date <= fy.endDate;
}

/** The calendar year whose rules (prisbasbelopp, tax rate) apply to a fiscal year: the year it ends in. */
export function rulesYearOf(fy: Pick<FiscalYear, 'endDate'>): number {
  return Number(fy.endDate.slice(0, 4));
}

export async function createFiscalYear(
  input: { companyId: Uuid; startDate: IsoDate; endDate: IsoDate },
  db: BookkeepingDB = defaultDb,
): Promise<FiscalYear> {
  if (input.endDate <= input.startDate) {
    throw new FiscalYearError('Fiscal year must end after it starts');
  }
  return db.transaction('rw', db.fiscalYears, async () => {
    const existing = await db.fiscalYears.where('companyId').equals(input.companyId).toArray();
    const overlaps = existing.some((fy) => input.startDate <= fy.endDate && input.endDate >= fy.startDate);
    if (overlaps) throw new FiscalYearError('Fiscal year overlaps an existing one');
    const fy: FiscalYear = {
      id: crypto.randomUUID(),
      companyId: input.companyId,
      startDate: input.startDate,
      endDate: input.endDate,
      status: 'open',
      label: fiscalYearLabel(input.startDate, input.endDate),
    };
    await db.fiscalYears.add(fy);
    return fy;
  });
}

export async function listFiscalYears(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<FiscalYear[]> {
  return db.fiscalYears.where('companyId').equals(companyId).sortBy('startDate');
}

export async function openFiscalYears(companyId: Uuid, db: BookkeepingDB = defaultDb): Promise<FiscalYear[]> {
  return (await listFiscalYears(companyId, db)).filter((fy) => fy.status === 'open');
}

export async function findFiscalYearForDate(
  companyId: Uuid,
  date: IsoDate,
  db: BookkeepingDB = defaultDb,
): Promise<FiscalYear | undefined> {
  const years = await db.fiscalYears.where('companyId').equals(companyId).toArray();
  return years.find((fy) => isWithin(date, fy));
}

/**
 * Mark a fiscal year closed. Logs a `closed_fiscal_year` event and, unless a
 * later lock already exists, a `year_closed` period lock through the year's
 * last day so the closed year is also locked for the period rules. Only the
 * year-end service should call this.
 */
export async function closeFiscalYear(fiscalYearId: Uuid, db: BookkeepingDB = defaultDb): Promise<void> {
  await db.transaction('rw', [db.fiscalYears, db.accountEvents, db.periodLocks], async () => {
    const fy = await db.fiscalYears.get(fiscalYearId);
    if (!fy) throw new FiscalYearError('Fiscal year not found');
    if (fy.status === 'closed') return;
    const now = new Date().toISOString();
    await db.fiscalYears.update(fiscalYearId, { status: 'closed' });
    await db.accountEvents.add({
      id: crypto.randomUUID(),
      companyId: fy.companyId,
      accountNumber: null,
      kind: 'closed_fiscal_year',
      fiscalYearId,
      at: now,
      details: { label: fy.label },
    });
    const latest = await lockedThroughFor(fy.companyId, db);
    if (latest === undefined || latest < fy.endDate) {
      await lockPeriod(
        { companyId: fy.companyId, fiscalYearId, periodKey: fiscalYearPeriodKey(fy), lockedThrough: fy.endDate, reason: 'year_closed' },
        db,
      );
    }
  });
}
